/**
 * 小票打印 —— 两条路,同一张票。
 *
 * ★ 蓝牙直连:一按出纸,不弹对话框。把票画成图发 ESC/POS,
 *   所以中文不用管打印机的码表(那是换一台机器就满屏乱码的老问题)。
 *   只有 Chrome / Edge / 安卓有 Web Bluetooth,iPhone 上没有。
 *
 * ★ 系统打印:排成 HTML 走浏览器的打印对话框。
 *   任何装在系统里的打印机都能用 —— USB、网口、已经配对好的蓝牙机。
 *   **网口机走的就是这条**:浏览器开不了到 9100 端口的 TCP 连接
 *   (这不是我们懒得做,是浏览器没有这个能力),
 *   而把网口机装进系统打印机之后,这条路一样能出纸。
 */
import { api } from './api';
import {
  buildReceipt,
  escposRaster,
  packMono,
  PAPER_DOTS,
  type PaperWidth,
  type ReceiptLine,
  type ReceiptOrder,
  type ReceiptSize,
  type ReceiptSizes,
} from '@delivery/shared';

export type PrintWay = 'system' | 'bluetooth' | 'server';

export interface PrinterSettings {
  way: PrintWay;
  paper: PaperWidth;
  /** 每一块的字号。没设的用默认 */
  sizes: ReceiptSizes;
  /** way=server 时用哪一台(设置里配的那些) */
  serverPrinterId?: string | null;
  /**
   * 这台设备自己调过排版。
   *
   * ★ 调过之后就**不再被店里那份盖掉**:
   *   没有这个标记的话,没权限改店铺设置的人(客服)在这台手机上
   *   调了字号 —— 保存不到服务端,而下次打开时服务端那份把它盖回去。
   *   现象是「我明明设过,每次都得重设一遍」。
   */
  layoutLocal?: boolean;
}

const KEY = 'printer.settings.v1';

export function loadPrinterSettings(): PrinterSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<PrinterSettings>;
      const okWay = p.way === 'system' || p.way === 'bluetooth' || p.way === 'server';
      if (okWay && (p.paper === '58' || p.paper === '80')) {
        return {
          way: p.way!,
          paper: p.paper,
          sizes: p.sizes ?? {},
          serverPrinterId: p.serverPrinterId ?? null,
          layoutLocal: p.layoutLocal === true,
        };
      }
    }
  } catch {
    /* 存坏了就当没设过 —— 打印设置不值得为它报错 */
  }
  /*
   * ★ 默认走系统打印。它在**所有**浏览器上都能用,
   *   而蓝牙那条路在 iPhone 上直接没有 —— 默认值不该是一条有人用不了的路。
   */
  return { way: 'system', paper: '80', sizes: {}, serverPrinterId: null, layoutLocal: false };
}

export function savePrinterSettings(s: PrinterSettings): void {
  localStorage.setItem(KEY, JSON.stringify(s));
}

export const bluetoothSupported = (): boolean =>
  typeof navigator !== 'undefined' && 'bluetooth' in navigator;

// ───────────────────────── 画成图 ─────────────────────────

/** 字号对应的像素高度(203dpi 下的观感,不是 CSS px) */
const FONT_PX: Record<ReceiptSize, number> = {
  // 'off' 的行在 buildReceipt 里就滤掉了,到不了这儿;给个值只为类型完整
  off: 0,
  small: 20,
  normal: 24,
  big: 30,
  huge: 44,
  giant: 60,
};
const PAD_X = 8;
/** 边框内侧留白 —— 文字到框线的距离 */
const BORDER_PAD = 6;
/** 边框距离上方上一个区域的外边距 —— 拉大距离更醒目 */
const BORDER_MARGIN_TOP = 22;
/** 边框距离下方下一个区域的外边距 */
const BORDER_MARGIN_BOTTOM = 8;

/**
 * 把小票画到 canvas 上。
 *
 * ★ 长地址要**自己折行**。交给 canvas 是没有折行的 ——
 *   它会一路画出纸外,而打印出来的结果是地址被裁掉半截,
 *   偏偏被裁掉的通常是门牌号那一段。
 */
export async function renderReceipt(
  lines: ReceiptLine[],
  paper: PaperWidth,
): Promise<HTMLCanvasElement> {
  const width = PAPER_DOTS[paper];
  const measure = document.createElement('canvas').getContext('2d')!;

  /*
   * ★ 图片那种二维码要先解码,所以这个函数是 async 的。
   *
   *   本来它是同步的,而「同步」在这儿不是什么值得守的东西:
   *   为了守住它,就得让调用方先把图片预加载好再传进来 ——
   *   于是「画一张票」这件事被拆成两半,而拆开的两半迟早会有人只调一半
   *   (表现是二维码那块是空白,别的都对)。
   *
   *   字符串那种二维码是矩阵,一格一格填黑,不需要解码 ——
   *   只有上传图片那条路真的要等。
   */
  const images = await loadQrImages(lines);

  type Draw = { text: string; line: ReceiptLine; y: number; h: number };
  const draws: Draw[] = [];
  let y = 8;

  let lastBordered = false;
  for (const line of lines) {
    if (line.bordered && !lastBordered) {
      y += BORDER_PAD + BORDER_MARGIN_TOP;
    } else if (!line.bordered && lastBordered) {
      y += BORDER_PAD + BORDER_MARGIN_BOTTOM;
    }
    lastBordered = !!line.bordered;

    if (line.rule) {
      draws.push({ text: '', line, y, h: 12 });
      y += 12;
      continue;
    }
    if (line.blank) {
      // 一行手写的高度,不是一行小字的高度 —— 留 10 像素等于没留
      y += 28;
      continue;
    }
    if (line.qr) {
      // 上下各留一点,不然码贴着上一行的字,扫的时候容易连带进去
      draws.push({ text: '', line, y: y + QR_PAD, h: line.qr.box });
      y += line.qr.box + QR_PAD * 2;
      continue;
    }
    const px = FONT_PX[line.size ?? 'normal'];
    measure.font = fontOf(line, px);
    /*
     * ★ 带边框的行文字要往里缩一点,不然贴着框线,挤得难看。
     */
    const textMaxW = line.bordered
      ? width - (PAD_X + BORDER_PAD) * 2
      : width - PAD_X * 2;
    for (const piece of wrap(measure, line.text, textMaxW)) {
      const h = Math.round(px * 1.32);
      draws.push({ text: piece, line, y, h });
      y += h;
    }
  }
  if (lastBordered) y += BORDER_PAD + BORDER_MARGIN_BOTTOM;
  y += 10;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  // ★ 高度也补到 8 的倍数 —— 光栅指令是按整行发的
  canvas.height = Math.ceil(y / 8) * 8;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.textBaseline = 'top';

  for (const d of draws) {
    if (d.line.rule) {
      ctx.fillStyle = '#000';
      ctx.fillRect(PAD_X, d.y + 5, width - PAD_X * 2, 2);
      continue;
    }
    if (d.line.qr) {
      drawQr(ctx, d.line.qr, width, d.y, images);
      continue;
    }
    const px = FONT_PX[d.line.size ?? 'normal'];
    ctx.font = fontOf(d.line, px);
    const w = ctx.measureText(d.text).width;
    /*
     * ★ 带边框的行,文字起点往里缩 BORDER_PAD,和框线留出间距。
     */
    const innerPad = d.line.bordered ? PAD_X + BORDER_PAD : PAD_X;
    const x = d.line.align === 'center' ? Math.max(innerPad, (width - w) / 2) : innerPad;
    /*
     * ★ 一律黑字,不铺黑底。
     *
     *   曾经「代收现金」和「冻品」那两行是白字黑底 —— 而在热敏纸上
     *   那是**看不见的**:浏览器打印默认不印背景,黑底被丢掉,
     *   只剩白字印在白纸上。用户看到的是那两行整行空白,
     *   而它们恰恰是最不能漏的两样。
     *   现在靠字号和加粗站出来,这是小票机上唯一不会消失的强调。
     */
    ctx.fillStyle = '#000';
    ctx.fillText(d.text, x, d.y + Math.round(px * 0.12));
  }

  /*
   * ★ 把连续的 bordered 行用一个矩形框起来。
   *   用 strokeRect 画线框 —— 纯黑线条在热敏纸上印得最清楚,
   *   不依赖背景色,什么打印机都不会丢。
   */
  {
    let i = 0;
    while (i < draws.length) {
      if (!draws[i]!.line.bordered) { i++; continue; }
      let j = i;
      while (j < draws.length && draws[j]!.line.bordered) j++;
      const top = draws[i]!.y - BORDER_PAD;
      const bottom = draws[j - 1]!.y + draws[j - 1]!.h + BORDER_PAD;
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.strokeRect(PAD_X, top, width - PAD_X * 2, bottom - top);
      i = j;
    }
  }

  return canvas;
}

/** 二维码上下各留这么多点 —— 贴着字的话扫的时候容易把字连带进去 */
const QR_PAD = 10;

/**
 * 二维码周围的**静区**,单位是模块。
 *
 * ★ 规范要求 4 个模块的白边。纸上看着像「留白留多了」,但少了它
 *   很多手机就是对不上焦 —— 而那种失败没有任何提示:
 *   码印得清清楚楚,人举着手机扫半天没反应,只会以为是手机的问题。
 */
const QR_QUIET = 4;

/**
 * 把票面里所有图片二维码解码好。
 *
 * ★ 一张解不出来就**跳过它**,不让整叠票打不出来。
 *   商家传了张坏图,代价该是「那块是空的,他一眼看得见」,
 *   而不是「打印按下去报错」—— 后者他只会理解成打印机坏了。
 */
async function loadQrImages(lines: ReceiptLine[]): Promise<Map<string, HTMLImageElement>> {
  const srcs = [...new Set(lines.map((l) => l.qr?.image).filter((v): v is string => !!v))];
  const out = new Map<string, HTMLImageElement>();
  await Promise.all(
    srcs.map(
      (src) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => {
            out.set(src, img);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = src;
        }),
    ),
  );
  return out;
}

/** 把二维码画到画布上,水平居中 */
function drawQr(
  ctx: CanvasRenderingContext2D,
  qr: NonNullable<ReceiptLine['qr']>,
  width: number,
  top: number,
  images: Map<string, HTMLImageElement>,
): void {
  const box = qr.box;
  const left = Math.max(0, Math.round((width - box) / 2));

  // 先铺白。静区靠的就是这块白 —— 纸本来是白的,但画布不是
  ctx.fillStyle = '#fff';
  ctx.fillRect(left, top, box, box);

  if (qr.image) {
    const img = images.get(qr.image);
    if (img) ctx.drawImage(img, left, top, box, box);
    return;
  }
  const m = qr.modules;
  if (!m || m.length === 0) return;

  /*
   * ★ 一格取整数个点。
   *
   *   不取整的话每一格的边落在半个点上,打印机按阈值二值化,
   *   结果是有的格子胖一点有的瘦一点 —— 印在纸上是歪的,扫不动。
   *   宁可整个码小一圈,也要让每一格一样大。
   */
  const n = m.length;
  const cell = Math.max(1, Math.floor(box / (n + QR_QUIET * 2)));
  const drawn = cell * n;
  const x0 = left + Math.round((box - drawn) / 2);
  const y0 = top + Math.round((box - drawn) / 2);

  ctx.fillStyle = '#000';
  for (let r = 0; r < n; r++) {
    const row = m[r]!;
    // 同一行连着的黑格合成一个矩形 —— 少几千次 fillRect,画得快也更整齐
    let c = 0;
    while (c < n) {
      if (row[c] !== '1') {
        c++;
        continue;
      }
      let end = c;
      while (end < n && row[end] === '1') end++;
      ctx.fillRect(x0 + c * cell, y0 + r * cell, (end - c) * cell, cell);
      c = end;
    }
  }
}

/**
 * 二维码矩阵 → 内联 SVG。
 *
 * ★ 系统打印和浏览器打印台两条路都排 HTML,用 SVG 而不是一堆 div:
 *   41×41 的码铺成 div 是一千六百个节点,一次打二十张就是三万多个,
 *   浏览器排版会卡住 —— 而卡住的表现是「按了打印,半天没反应」。
 *
 * ★ `shape-rendering="crispEdges"`:不抗锯齿。抗锯齿会在每一格边上
 *   抹一圈灰,热敏机二值化之后格子边界就糊了。
 *
 * ★ viewBox 往外扩 QR_QUIET 个模块,就是那圈静区(见上面的说明)。
 *
 * ★ station.html 里有一份**一模一样的**实现(那一页是 ES5、零依赖,
 *   import 不了这个包)。改这儿的时候记得一起改 —— 两边印出来
 *   必须是同一个码。
 */
export function qrSvg(modules: string[], mm: number): string {
  const n = modules.length;
  const side = n + QR_QUIET * 2;
  let rects = '';
  for (let r = 0; r < n; r++) {
    const row = modules[r]!;
    let c = 0;
    while (c < n) {
      if (row[c] !== '1') {
        c++;
        continue;
      }
      let end = c;
      while (end < n && row[end] === '1') end++;
      rects += `<rect x="${QR_QUIET + c}" y="${QR_QUIET + r}" width="${end - c}" height="1"/>`;
      c = end;
    }
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${mm}mm" height="${mm}mm" ` +
    `viewBox="0 0 ${side} ${side}" shape-rendering="crispEdges">` +
    `<rect width="${side}" height="${side}" fill="#fff"/>` +
    `<g fill="#000">${rects}</g></svg>`
  );
}

function fontOf(line: ReceiptLine, px: number): string {
  const weight = line.bold || line.size !== 'normal' ? '700' : '400';
  // 小票机是点阵,笔画细的字体印出来会糊 —— 用系统里最粗实的那几个
  return `${weight} ${px}px "PingFang SC", "Microsoft YaHei", "Noto Sans SC", sans-serif`;
}

/**
 * 按像素宽度折行。
 *
 * ★ 中文没有空格,所以底子上是**按字符**退。
 *   但英文地址不能这么退:「Auckland」被劈成「Auc / kland」之后,
 *   司机要在门口把两行拼回去才读得出这是哪儿 ——
 *   而地址是这张纸上最要紧的一行。
 *   所以退到行尾时,如果这一行里还有空格,就退到那个空格。
 */
function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  if (text === '') return [''];
  const out: string[] = [];
  let cur = '';
  for (const ch of text) {
    const next = cur + ch;
    if (ctx.measureText(next).width > maxWidth && cur !== '') {
      const cut = cur.lastIndexOf(' ');
      // 只在空格离行尾不远时才回退 —— 一整行都是中文时 cut 是 -1,
      // 而回退过头会把前半行也甩到下一行去
      if (cut > 0 && cur.length - cut <= 14) {
        out.push(cur.slice(0, cut));
        cur = cur.slice(cut + 1) + ch;
      } else {
        out.push(cur);
        cur = ch === ' ' ? '' : ch;
      }
    } else {
      cur = next;
    }
  }
  if (cur !== '') out.push(cur);
  return out;
}

/** canvas → ESC/POS 字节 */
export function receiptBytes(canvas: HTMLCanvasElement): Uint8Array {
  const ctx = canvas.getContext('2d')!;
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const gray = new Uint8Array(canvas.width * canvas.height);
  for (let i = 0, p = 0; i < img.data.length; i += 4, p++) {
    // 感知亮度。按平均值算的话,红底白字这种会算出中灰,印出来一片糊
    gray[p] = (img.data[i]! * 299 + img.data[i + 1]! * 587 + img.data[i + 2]! * 114) / 1000;
  }
  const bits = packMono(gray, canvas.width, canvas.height);
  return escposRaster(bits, canvas.width, canvas.height);
}

/**
 * 字节 → base64。
 *
 * ★ 分片转,不是 `String.fromCharCode(...bytes)` 一把梭 ——
 *   一张小票有十几万字节,展开成十几万个参数会把调用栈撑爆
 *   (表现是「打印偶尔整个页面卡死」,而且只在票长的时候)。
 */
/** 字符串 → base64(先转 UTF-8)。直接 btoa 会在中文上抛 */
function utf8ToBase64(s: string): string {
  return toBase64(new TextEncoder().encode(s));
}

function toBase64(bytes: Uint8Array): string {
  let s = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(s);
}

// ───────────────────────── 蓝牙 ─────────────────────────

/*
 * ★ 常见 ESC/POS 蓝牙小票机用的服务号。
 *   没有统一标准,各家按自己的来 —— 所以是一串挨个试,
 *   而不是写死一个。写死一个的表现是「我的打印机连不上」,
 *   而连不上和「坏了」在用户看来是一回事。
 */
const PRINTER_SERVICES = [
  0x18f0, // 大部分国产 58mm 机
  0xff00,
  0xffe0,
  0xfee7, // 部分佳博 / 芯烨
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip 透传(很多机器用这块模组)
];

let device: BluetoothDevice | null = null;
let characteristic: BluetoothRemoteGATTCharacteristic | null = null;

export function connectedPrinterName(): string | null {
  return device?.gatt?.connected ? (device.name ?? '已连接的打印机') : null;
}

/** 让人挑一台打印机。必须由点击触发 —— 浏览器不给非手势调起选择框 */
export async function pickPrinter(): Promise<string> {
  if (!bluetoothSupported()) throw new Error('这个浏览器不支持蓝牙打印,换 Chrome 或者用系统打印');
  const d = await navigator.bluetooth.requestDevice({
    // 挑机器时不过滤:一过滤,不广播这些服务号的机器在列表里**根本不出现**,
    // 而人看着自己的打印机没出现,只会以为坏了
    acceptAllDevices: true,
    optionalServices: PRINTER_SERVICES,
  });
  device = d;
  characteristic = null;
  await ensureConnected();
  return d.name ?? '打印机';
}

async function ensureConnected(): Promise<BluetoothRemoteGATTCharacteristic> {
  if (characteristic && device?.gatt?.connected) return characteristic;
  if (!device) throw new Error('还没选打印机');

  const gatt = await device.gatt!.connect();
  const services = await gatt.getPrimaryServices();
  for (const s of services) {
    const chars = await s.getCharacteristics();
    const writable = chars.find((c) => c.properties.write || c.properties.writeWithoutResponse);
    if (writable) {
      characteristic = writable;
      return writable;
    }
  }
  throw new Error('这台设备上找不到可以写数据的通道 —— 它可能不是小票打印机');
}

/**
 * 分片发出去。
 *
 * ★ BLE 一次最多发 20 字节(协商过 MTU 的能多些),而一张票是几万字节。
 *   一口气 write 过去的表现是:打印机吐出前面几行然后停住 ——
 *   看起来像「打印机坏了」,其实是缓冲区被冲爆了。
 *   所以切片,而且每片之间留一点时间给它打。
 */
async function send(bytes: Uint8Array): Promise<void> {
  const ch = await ensureConnected();
  const CHUNK = 180;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.slice(i, i + CHUNK);
    if (ch.properties.writeWithoutResponse) await ch.writeValueWithoutResponse(slice);
    else await ch.writeValue(slice);
    await new Promise((r) => setTimeout(r, 12));
  }
}

// ───────────────────────── 对外 ─────────────────────────

export interface PrintJob {
  order: ReceiptOrder;
}

/**
 * 打一叠票。
 *
 * ★ 一次调用打完整叠,而不是循环调 N 次:
 *   系统打印那条路每调一次就弹一次对话框,
 *   选中二十单的话人要按二十次「打印」。
 */
export async function printReceipts(
  orders: ReceiptOrder[],
  settings: PrinterSettings,
  opts: { timeZone?: string; serverPrinterKind?: string } = {},
): Promise<void> {
  if (orders.length === 0) return;
  const sheets = orders.map((o) =>
    buildReceipt(o, {
      now: new Date(),
      locale: 'zh-CN',
      timeZone: opts.timeZone,
      sizes: settings.sizes,
    }),
  );

  if (settings.way === 'bluetooth') {
    for (const lines of sheets) {
      await send(receiptBytes(await renderReceipt(lines, settings.paper)));
    }
    await reportPrinted(orders);
    return;
  }

  /*
   * ★ 后台打印:把排好版的**字节**送到服务器的队列上,
   *   由打印机那一侧来取。不弹打印框,手机和 iPad 上一样能按。
   *
   *   送字节而不是送订单 id —— 取任务时才渲染的话,那会儿订单可能已经改了,
   *   打出来的和按下打印这一刻看到的不是同一张。纸上那张是凭证。
   */
  if (settings.way === 'server') {
    if (!settings.serverPrinterId) throw new Error('还没选后台打印机');
    /*
     * ★ 打印台(收银机上开着的那个标签页)吃的是**排版内容**,不是 ESC/POS 字节 ——
     *   它要靠浏览器把这张票排成 HTML 再交给系统打印机。
     *   发字节过去的话,它只能干瞪眼。
     */
    const toStation = opts.serverPrinterKind === 'STATION';
    /*
     * ★ 顺序发,不并发。
     *
     *   ESC/POS 那条要把每张票画成图(图片二维码还要等解码),
     *   并发起来二十张票同时占着二十块画布 —— 在收银机那种老机器上
     *   会把内存顶掉,而表现是标签页整个崩掉,人只看到「页面没反应了」。
     */
    const jobs: Array<{ bytes: string; format: string; orderNo: string | null }> = [];
    for (let i = 0; i < sheets.length; i++) {
      const lines = sheets[i]!;
      jobs.push({
        bytes: toStation
          ? utf8ToBase64(JSON.stringify({ lines, paper: settings.paper }))
          : toBase64(receiptBytes(await renderReceipt(lines, settings.paper))),
        format: toStation ? 'RECEIPT_JSON' : 'ESCPOS',
        orderNo: orders[i]?.orderNo ?? null,
      });
    }
    await postJobsInBatches(settings.serverPrinterId, jobs);
    return;
  }

  printViaSystem(sheets, settings.paper);
  await reportPrinted(orders);
}

/**
 * 一次最多发多少张。服务端的 `jobs` 数组上限就是 50。
 */
const MAX_JOBS_PER_POST = 50;

/**
 * 一次请求的 body 最多多大(字节)。
 *
 * ★ 服务端是 2MB(apps/api/src/main.ts 里的 `limit: '2mb'`),这里留了余量
 *   给 JSON 的引号、逗号和外面那一层字段 —— 卡得刚好的话,
 *   差几十个字节就是一个 413,而 413 在界面上只会变成「打印失败」。
 */
const MAX_POST_BYTES = 1_400_000;

/**
 * 把打印任务按**体积**分批发出去。
 *
 * ★ 为什么非分不可:小票里可以内联一张商家二维码图,而那张图是
 *   **每张票各带一份**的。一次选五十单,body 就是五十份 ——
 *   超过服务端上限时整个请求被拒,一张都发不出去,
 *   而人看到的是「打印失败」,跟「那张二维码图有点大」之间
 *   没有任何看得出来的联系。
 *
 *   这和上一次浏览器打印台那个 bug 是同一个形状:**错在一头,现象在另一头**。
 *   所以这里不去赌「应该不会超」,而是按实际字节数切。
 *
 * ★ 顺序发,不并发:它们要按顺序进同一台打印机的队列,
 *   而并发发出去的话到达顺序不定 —— 一叠票的顺序乱了,
 *   仓库那边是按顺序贴到框上的。
 *
 * ★ 单张就超上限时**照样发**(而不是静静丢掉):让服务端去拒,
 *   人至少能从报错里知道是哪一单。悄悄跳过一张票,
 *   他要到发现少贴了一个框才知道。
 */
async function postJobsInBatches(
  printerId: string,
  jobs: Array<{ bytes: string; format: string; orderNo: string | null }>,
): Promise<void> {
  let batch: typeof jobs = [];
  let size = 0;

  const flush = async (): Promise<void> => {
    if (batch.length === 0) return;
    await api('/print-jobs', { method: 'POST', body: { printerId, jobs: batch } });
    batch = [];
    size = 0;
  };

  for (const job of jobs) {
    const cost = job.bytes.length + 120; // 120 ≈ 外面那几个字段和 JSON 标点
    if (batch.length > 0 && (size + cost > MAX_POST_BYTES || batch.length >= MAX_JOBS_PER_POST)) {
      await flush();
    }
    batch.push(job);
    size += cost;
  }
  await flush();
}

/**
 * 告诉服务端「这几单打出来了」。
 *
 * ★ 系统打印和蓝牙这两条走的是浏览器,服务端收不到任何回音 ——
 *   不说一声的话,这几单永远留在「没打过小票」里,
 *   而下一次批量选会把它们又选进去,重复打一遍。
 *   (后台打印那条不用管:打印机回话时服务端自己标。)
 *
 * ★ 失败不抛。纸已经出来了,这一步只是记账 ——
 *   为了记账失败而告诉人「打印失败」,他会再按一次。
 */
async function reportPrinted(orders: ReceiptOrder[]): Promise<void> {
  const orderNos = orders.map((o) => o.orderNo).filter(Boolean);
  if (orderNos.length === 0) return;
  try {
    await api('/print-jobs/mark-printed', { method: 'POST', body: { orderNos } });
  } catch {
    /* 记不上就算了 —— 纸已经出来了 */
  }
}

/**
 * 走系统打印对话框。
 *
 * ★ 用 iframe 而不是新开窗口:新窗口会被拦截器挡掉(而且挡掉的时候
 *   一声不吭),iframe 不会。打完把它拆掉,不然打第二次时页面上
 *   挂着一堆看不见的 iframe。
 */
function printViaSystem(sheets: ReceiptLine[][], paper: PaperWidth): void {
  const mm = paper === '58' ? 58 : 80;
  const html = `<!doctype html><html><head><meta charset="utf-8"><style>
    @page { size: ${mm}mm auto; margin: 0; }
    html, body { margin: 0; padding: 0; background: #fff; }
    .sheet {
      width: ${mm - 6}mm; padding: 3mm; color: #000;
      font-family: "PingFang SC", "Microsoft YaHei", "Noto Sans SC", sans-serif;
      /* ★ 每张一页。不断页的话二十张票连着印成一条长纸,撕都没法撕 */
      page-break-after: always; break-after: page;
    }
    .sheet:last-child { page-break-after: auto; break-after: auto; }
    .l { font-size: 11pt; line-height: 1.35; word-break: break-word; }
    .small { font-size: 9pt; }
    .big { font-size: 14pt; font-weight: 700; }
    .huge { font-size: 20pt; font-weight: 700; }
    .giant { font-size: 27pt; font-weight: 700; }
    .c { text-align: center; }
    /*
      ★ 不用「白字黑底」——**浏览器打印默认不印背景色**。
        背景没了,剩下白字印在白纸上:整行看不见,而那两行恰恰是
        「冻品」和「代收现金」。用户报的就是这个。

        「print-color-adjust: exact」能强制印背景,但它依赖打印机驱动
        老不老实 —— 而这一行漏掉的代价是货化了、钱没收着。
        所以换成不依赖任何背景的做法:粗黑框 + 黑粗字,
        黑字在什么打印机上都印得出来。
    */
    /*
      ★ 只加粗,不加底色也不加框。
        白字黑底在热敏纸上是看不见的(浏览器打印默认不印背景);
        加框试过一版,用户看了实物说框也不要 —— 那就只剩字本身,
        而这是在小票机上唯一一种不会消失的强调。
    */
    .strong { font-weight: 700; color: #000; }
    .rule { border-top: 1px solid #000; margin: 1.5mm 0; }
    .bordered-group { border: 1px solid #000; padding: 1.5mm 2mm; margin: 3.5mm 0 1.5mm 0; }
  </style></head><body>${sheets.map(sheetHtml).join('')}</body></html>`;

  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(frame);
  const doc = frame.contentDocument!;
  doc.open();
  doc.write(html);
  doc.close();

  const go = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    // 打印对话框是同步阻塞的,但 Safari 上不是 —— 给它留点时间再拆
    setTimeout(() => frame.remove(), 60_000);
  };
  if (doc.readyState === 'complete') go();
  else frame.onload = go;
}

/**
 * 二维码那一块的 HTML。
 *
 * ★ `box` 是打印点(203dpi),除以 8 就是毫米 —— 这个换算只在这儿做一次,
 *   不散在各处:散开之后总有一处会写成别的数,
 *   而表现是「预览和纸上不一样大」,没人会想到去对这个除法。
 *
 * ★ 图片那条走 `<img>`:它已经是内联的 data: URI,不会去连网络 ——
 *   而打印对话框弹出来时如果还在等一张网络图,浏览器会先印一个空框。
 *
 * ★ station.html 里有一份**一模一样的**实现,改这儿记得一起改。
 */
function qrHtml(qr: NonNullable<ReceiptLine['qr']>): string {
  const mm = qr.box / 8;
  const inner = qr.image
    ? `<img src="${escapeHtml(qr.image)}" alt="" style="width:${mm}mm;height:${mm}mm;display:block" />`
    : qr.modules
      ? qrSvg(qr.modules, mm)
      : '';
  if (!inner) return '';
  return `<div style="display:flex;justify-content:center;margin:2mm 0">${inner}</div>`;
}

function sheetHtml(lines: ReceiptLine[]): string {
  /*
   * ★ 连续的 bordered 行包在同一个框里,不是每行画一个 ——
   *   一行一个框的话,备注和区域各有各的边,看起来像两块不相干的东西,
   *   而它们合在一起才是「这箱货的附加信息」。
   */
  let body = '';
  let borderedBuf = '';
  const flushBordered = () => {
    if (borderedBuf) {
      body += `<div class="bordered-group">${borderedBuf}</div>`;
      borderedBuf = '';
    }
  };

  for (const l of lines) {
    let piece: string;
    if (l.rule) { piece = '<div class="rule"></div>'; }
    else if (l.blank) { piece = '<div style="height:5mm"></div>'; }
    else if (l.qr) { piece = qrHtml(l.qr); }
    else {
      const cls = [
        'l',
        l.size && l.size !== 'normal' ? l.size : '',
        l.align === 'center' ? 'c' : '',
        l.strong ? 'strong' : '',
      ]
        .filter(Boolean)
        .join(' ');
      piece = `<div class="${cls}">${escapeHtml(l.text)}</div>`;
    }

    if (l.bordered) {
      borderedBuf += piece;
    } else {
      flushBordered();
      body += piece;
    }
  }
  flushBordered();

  return `<div class="sheet">${body}</div>`;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
